import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/Button';
import type { BootstrapProgress } from '../sync/engine';
import { useFormat, useT, type BoundFormatters } from '../i18n';
import styles from './ContentSetupOverlay.module.css';

// Gemeinsames Vollbild-Setup-Overlay (Balken + ETA/Bytes + rotierende Tipps,
// Gold-Schimmer). Nach mehreren gescheiterten Daten-Versuchen wechselt es auf
// eine Fehler-Ansicht mit Ausweg (siehe ERROR_AFTER_ATTEMPTS / onDismiss);
// solange es laedt, gibt es weiterhin KEIN „Trotzdem fortfahren". Wird von zwei
// Stellen genutzt:
//  1. ContentSetupOverlay (Daten/Asset-Bootstrap, Phasen 'data'/'assets')
//  2. OtaProvider (OTA-Bundle-Download, Phase 'bundle')
// So bleibt die Optik identisch (DRY), die Texte (Titel/Hint) sind je Aufrufer
// konfigurierbar.

// Rotierende Tipps unter dem Balken (i18n). Reihenfolge zufaellig, derselbe
// Tipp nie zweimal direkt hintereinander.
const TIP_KEYS = [
  'app.content_setup_tip_1',
  'app.content_setup_tip_2',
  'app.content_setup_tip_3',
  'app.content_setup_tip_4',
  'app.content_setup_tip_5',
  'app.content_setup_tip_6',
  'app.content_setup_tip_7',
  'app.content_setup_tip_8',
] as const;
const TIP_ROTATE_MS = 4_500;
// Warum: erst nach mehreren erfolglosen Anlaeufen (Backoff 5s/10s/20s) auf die
// Fehler-Ansicht wechseln — ein einzelner Aussetzer heilt sich meist selbst.
const ERROR_AFTER_ATTEMPTS = 3;
// Warum: die Fehler-Ansicht braucht als einzige eine Knopfreihe; das CSS-Modul
// gehoert zum Balken-Layout und bleibt unangetastet.
const ERROR_ACTIONS_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};
// CRIT 6: dezente, immer sichtbare Ausweg-Reihe waehrend des Ladens. Der
// Download laeuft im Hintergrund weiter (der Aufrufer setzt nur `dismissed`,
// der Status bleibt 'bootstrapping') — es verschwindet nur das Vollbild.
const SKIP_ROW_STYLE: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginBlockStart: '4px',
};

// MB mit einer Nachkommastelle, lokalisiertes Dezimalzeichen. Robust gegen
// NaN/negative Werte (zeigt dann 0,0 statt „NaN").
function formatMb(bytes: number, formatNumber: BoundFormatters['fmtNumber']): string {
  const safe = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  const mb = safe / (1024 * 1024);
  return formatNumber(mb, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

export interface ProgressSetupOverlayProps {
  // Aktueller Fortschritt (null = noch keiner; Balken steht dann am 6%-Floor).
  progress: BootstrapProgress | null;
  // Hauptueberschrift (i18n-Key, bereits aufgeloest vom Aufrufer? Nein —
  // wir nehmen Keys entgegen und uebersetzen hier, damit ein Sprachwechsel
  // greift).
  titleKey?: string;
  hintKey?: string;
  // Wortloser Startzustand fuer den OTA-Check: nur dunkle Flaeche und Schimmer,
  // solange noch gar nicht feststeht, ob eine Aktualisierung existiert.
  silent?: boolean;
  // Tipps rotieren nur bei langen Phasen (Asset/Content). Fuer den kurzen
  // OTA-Download bleiben sie aus (showTips=false), damit kein Flackern auftritt.
  showTips?: boolean;
  // Ausweg aus einer dauerhaft gescheiterten Daten-Phase ("Ohne Offline-Modus
  // fortfahren"). NUR der Web-Aufrufer setzt ihn: dort funktioniert der reine
  // Online-Pfad (useLocalFirstList faellt auf fetchAndSeed zurueck). In der
  // nativen App gibt es diesen Knopf bewusst nicht — eine leere lokale DB
  // erzeugt dort Folgefehler in der ganzen App.
  onDismiss?: () => void;
}

export function ProgressSetupOverlay({
  progress,
  titleKey,
  hintKey,
  silent = false,
  showTips = true,
  onDismiss,
}: ProgressSetupOverlayProps) {
  const { t } = useT();
  const { fmtNumber } = useFormat();
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * TIP_KEYS.length));
  // Warum: einmal gezeigt, bleibt die Fehler-Ansicht stehen. Der Retry laeuft
  // im Hintergrund weiter — ohne diesen Haken sprang die Anzeige bei jedem
  // Versuch zwischen Balken und Fehlertext hin und her. Gelingt der Bootstrap,
  // verschwindet das Overlay ohnehin komplett (Status wechselt auf 'live').
  const [showError, setShowError] = useState(false);

  const dataError = progress?.dataError === true;
  const failedAttempts = progress?.failedAttempts ?? 0;
  useEffect(() => {
    if (dataError && failedAttempts >= ERROR_AFTER_ATTEMPTS) setShowError(true);
  }, [dataError, failedAttempts]);

  // Tipp-Rotation nur, solange das Overlay sichtbar ist + Tipps gewuenscht
  // (kein Interval-Leak).
  useEffect(() => {
    if (silent || !showTips || showError) return undefined;
    const id = window.setInterval(() => {
      setTipIndex((prev) => {
        if (TIP_KEYS.length <= 1) return prev;
        let next = prev;
        while (next === prev) next = Math.floor(Math.random() * TIP_KEYS.length);
        return next;
      });
    }, TIP_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [showError, showTips, silent]);

  const view = useMemo(() => ({
    percentage: Math.max(0, Math.min(100, Math.round(progress?.percentage ?? 0))),
    phase: progress?.phase ?? 'data',
    bytesDone: progress?.bytesDone ?? 0,
    bytesTotal: progress?.bytesTotal ?? 0,
    etaSeconds: progress?.etaSeconds ?? null,
    waitingForNetwork: progress?.waitingForNetwork ?? false,
  }), [progress]);

  // Echte Dateimenge zeigen, sobald bytesTotal bekannt ist (Asset- ODER
  // Bundle-Phase); sonst die ETA.
  const showSize = view.bytesTotal > 0 && (view.phase === 'assets' || view.phase === 'bundle');
  const showEta = !view.waitingForNetwork && view.etaSeconds !== null && view.etaSeconds > 0;
  const tipKey = TIP_KEYS[tipIndex] ?? TIP_KEYS[0];

  let detail = '';
  if (showSize) {
    detail = t('app.content_setup_size', {
      done: formatMb(view.bytesDone, fmtNumber),
      total: formatMb(view.bytesTotal, fmtNumber),
    });
  } else if (showEta) {
    detail = t('app.content_setup_eta', { s: view.etaSeconds ?? 0 });
  }

  // Ehrliche Fehler-Ansicht statt eines Balkens, der nie ankommt: der Retry
  // laeuft im Hintergrund weiter, der User bekommt aber endlich einen Ausweg
  // („Neu laden" immer, „ohne Offline-Modus" nur im Web — siehe onDismiss).
  if (showError) {
    return (
      <div className={styles.overlay} role="alert">
        <div className={styles.content}>
          <div className={styles.title}>{t('app.content_setup_error_title')}</div>
          <div className={styles.hint}>{t('app.content_setup_error_text')}</div>
          <div className={styles.progressBlock} style={ERROR_ACTIONS_STYLE}>
            <Button variant="primary" fullWidth onClick={() => window.location.reload()}>
              {t('app.content_setup_error_reload')}
            </Button>
            {onDismiss ? (
              <Button variant="secondary" fullWidth onClick={onDismiss}>
                {t('app.content_setup_error_continue')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} role="status" aria-live="polite" aria-busy="true">
      <div className={silent ? `${styles.content} ${styles.silentContent}` : styles.content}>
        {!silent && titleKey ? <div className={styles.title}>{t(titleKey)}</div> : null}
        {!silent && hintKey ? <div className={styles.hint}>{t(hintKey)}</div> : null}
        <div className={styles.progressBlock}>
          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={view.percentage}
            aria-label={t('app.content_setup_progress_label', { percent: view.percentage })}
          >
            {/* Warum: min. 6% Fuellbreite, damit der Dauer-Schimmer immer eine
                sichtbare Flaeche hat (auch ganz am Anfang bei 0–4%). */}
            <div className={styles.progressFill} style={{ width: `${Math.max(6, view.percentage)}%` }} />
          </div>
          {!silent ? (
            <div className={styles.progressMeta}>
              <span className={styles.progressPercent}>{view.percentage}%</span>
              {detail ? <span className={styles.progressDetail}>{detail}</span> : null}
            </div>
          ) : null}
        </div>
        {!silent && view.waitingForNetwork ? (
          <div className={styles.waitingHint}>{t('app.content_setup_waiting')}</div>
        ) : null}
        {!silent && showTips ? <div key={tipIndex} className={styles.tip}>{t(tipKey)}</div> : null}
        {/* CRIT 6: „Überspringen" — nur wenn der Aufrufer einen Ausweg erlaubt
            (Web; nativ bewusst nicht, weil eine leere lokale DB dort Folgefehler
            erzeugt). Der Bootstrap laeuft im Hintergrund weiter. */}
        {!silent && onDismiss ? (
          <div style={SKIP_ROW_STYLE}>
            <Button variant="secondary" onClick={onDismiss}>
              {t('app.content_setup_skip')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
